import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Car, 
  ArrowLeft, 
  Zap, 
  Leaf, 
  Fuel,
  Battery,
  Info,
  CheckCircle2,
  RotateCcw,
  Search,
  Loader2,
  Calendar,
  Plus,
  Trash2,
  Users,
  Download,
  FileText,
  FileSpreadsheet
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fuelTypeConfig = {
  elettrico: { label: "Full Electric", icon: <Zap className="w-5 h-5" />, percentage: 10, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  plugin_hybrid: { label: "Plug-in Hybrid", icon: <Leaf className="w-5 h-5" />, percentage: 20, color: "bg-blue-100 text-blue-700 border-blue-200" },
  benzina: { label: "Benzina", icon: <Fuel className="w-5 h-5" />, percentage: 50, color: "bg-amber-100 text-amber-700 border-amber-200" },
  diesel: { label: "Diesel", icon: <Fuel className="w-5 h-5" />, percentage: 50, color: "bg-slate-100 text-slate-700 border-slate-200" },
  ibrido_benzina: { label: "Ibrido Benzina", icon: <Battery className="w-5 h-5" />, percentage: 50, color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  ibrido_diesel: { label: "Ibrido Diesel", icon: <Battery className="w-5 h-5" />, percentage: 50, color: "bg-teal-100 text-teal-700 border-teal-200" },
};

const CalculatorPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("single"); // "single" or "fleet"
  
  // Fleet state
  const [fleet, setFleet] = useState([]);
  
  // Single calculation result
  const [result, setResult] = useState(null);
  
  // Form state
  const [orderType, setOrderType] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [marca, setMarca] = useState("");
  const [modello, setModello] = useState("");
  const [costoKm, setCostoKm] = useState("");
  
  // Riassegnazione specific
  const [isPre2025, setIsPre2025] = useState(null);
  const [canoneNoleggio, setCanoneNoleggio] = useState("");
  const [optionalDipendente, setOptionalDipendente] = useState("");
  const [kmMensili, setKmMensili] = useState("");
  
  // Autocomplete state
  const [modelloSearch, setModelloSearch] = useState("");
  const [modelloSuggestions, setModelloSuggestions] = useState([]);
  const [showModelloDropdown, setShowModelloDropdown] = useState(false);
  const [searchingModello, setSearchingModello] = useState(false);
  const modelloInputRef = useRef(null);
  
  // Data from API
  const [marche, setMarche] = useState([]);

  // Load marche on mount
  useEffect(() => {
    const loadMarche = async () => {
      try {
        await axios.post(`${API}/seed-aci-tables`);
        const response = await axios.get(`${API}/aci-tables/marche`);
        setMarche(response.data);
      } catch (error) {
        console.error("Error loading marche:", error);
      }
    };
    loadMarche();
  }, []);

  // Debounced search for modello
  const searchModelli = useCallback(async (searchTerm) => {
    if (!marca || searchTerm.length < 1) {
      setModelloSuggestions([]);
      return;
    }
    
    setSearchingModello(true);
    try {
      const response = await axios.get(`${API}/aci-tables/search-modelli`, {
        params: { marca, q: searchTerm, limit: 20 }
      });
      setModelloSuggestions(response.data);
    } catch (error) {
      console.error("Error searching modelli:", error);
    } finally {
      setSearchingModello(false);
    }
  }, [marca]);

  // Debounce effect for modello search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modelloSearch && marca) {
        searchModelli(modelloSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [modelloSearch, marca, searchModelli]);

  // Load initial modelli when marca changes
  useEffect(() => {
    const loadInitialModelli = async () => {
      if (!marca) {
        setModelloSuggestions([]);
        setModello("");
        setModelloSearch("");
        setCostoKm("");
        setFuelType("");
        return;
      }
      try {
        const response = await axios.get(`${API}/aci-tables/modelli/${encodeURIComponent(marca)}`, {
          params: { limit: 20 }
        });
        setModelloSuggestions(response.data);
      } catch (error) {
        console.error("Error loading modelli:", error);
      }
    };
    loadInitialModelli();
  }, [marca]);

  // Handle modello selection
  const handleModelloSelect = (selected) => {
    setModello(selected.modello);
    setModelloSearch(selected.modello);
    setCostoKm(selected.costo_km.toString());
    if (selected.alimentazione) {
      setFuelType(selected.alimentazione);
    }
    setShowModelloDropdown(false);
    
    const perc = fuelTypeConfig[selected.alimentazione]?.percentage || 50;
    const fbKey = `fb_${perc}`;
    const fbValue = selected[fbKey];
    if (fbValue) {
      toast.success(`Costo km: €${selected.costo_km.toFixed(4)}/km`);
    }
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const payload = {
        tipo_ordine: orderType,
        alimentazione: fuelType,
        marca: marca || null,
        modello: modello || null,
        costo_km: costoKm ? parseFloat(costoKm) : null,
      };

      if (orderType === "riassegnazione") {
        payload.data_immatricolazione = isPre2025 ? "2024-01-01" : "2025-06-01";
        if (isPre2025) {
          payload.canone_noleggio = parseFloat(canoneNoleggio);
          payload.optional_dipendente = optionalDipendente ? parseFloat(optionalDipendente) : 0;
          payload.km_lavorativi_mese = parseFloat(kmMensili);
        }
      }

      const response = await axios.post(`${API}/calculate`, payload);
      
      if (mode === "fleet") {
        // Add to fleet
        const vehicleEntry = {
          id: Date.now(),
          marca,
          modello,
          alimentazione: fuelType,
          orderType,
          isPre2025: orderType === "riassegnazione" ? isPre2025 : null,
          fringe_benefit_annuo: response.data.fringe_benefit_annuo,
          fringe_benefit_mensile: response.data.fringe_benefit_mensile,
          percentuale: response.data.percentuale_applicata,
          metodo: response.data.metodo_calcolo,
          dettagli: response.data.dettagli
        };
        setFleet(prev => [...prev, vehicleEntry]);
        toast.success(`${marca} ${modello.substring(0, 20)} aggiunto alla flotta!`);
        // Reset form for next vehicle
        resetForm();
      } else {
        setResult(response.data);
        toast.success("Calcolo completato!");
      }
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error(error.response?.data?.detail || "Errore nel calcolo");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOrderType("");
    setFuelType("");
    setMarca("");
    setModello("");
    setModelloSearch("");
    setCostoKm("");
    setIsPre2025(null);
    setCanoneNoleggio("");
    setOptionalDipendente("");
    setKmMensili("");
    setModelloSuggestions([]);
  };

  const handleReset = () => {
    resetForm();
    setResult(null);
    setFleet([]);
  };

  const removeFromFleet = (id) => {
    setFleet(prev => prev.filter(v => v.id !== id));
    toast.info("Veicolo rimosso dalla flotta");
  };

  // Get Tipo Operazione label
  const getTipoOperazione = (vehicle) => {
    if (vehicle.orderType === "nuovo_ordine") return "Nuovo Ordine";
    if (vehicle.isPre2025) return "Riassegnazione (Pre 2025)";
    return "Riassegnazione (Post 2025)";
  };

  // Helper function to trigger download
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export PDF - Single Vehicle
  const exportSinglePDF = () => {
    if (!result) return;
    
    try {
      const doc = new jsPDF();
      const today = new Date().toLocaleDateString('it-IT');
      const tipoOp = orderType === "nuovo_ordine" ? "Nuovo Ordine" : (isPre2025 ? "Riassegnazione (Pre 2025)" : "Riassegnazione (Post 2025)");
      
      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.text("FringeCalculator", 20, 25);
      doc.setFontSize(10);
      doc.text("Calcolo del " + today, 150, 25);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.text("Calcolo Fringe Benefit", 20, 55);
      
      // Table header
      doc.setFillColor(241, 245, 249);
      doc.rect(20, 65, 170, 10, 'F');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text("MARCA", 25, 72);
      doc.text("MODELLO", 55, 72);
      doc.text("TIPO OPERAZIONE", 100, 72);
      doc.text("ANNUO", 150, 72);
      doc.text("MENSILE", 175, 72);
      
      // Table row
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(marca || "-", 25, 85);
      doc.text((modello || "-").substring(0, 20), 55, 85);
      doc.text(tipoOp, 100, 85);
      doc.text("EUR " + result.fringe_benefit_annuo.toFixed(2), 150, 85);
      doc.text("EUR " + result.fringe_benefit_mensile.toFixed(2), 175, 85);
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text("Documento generato da FringeCalculator - Basato su Tabelle ACI 2026", 20, 280);
      doc.text("Azienda: " + (user?.azienda || 'N/A'), 20, 285);
      
      // Generate blob and download
      const pdfBlob = doc.output('blob');
      const filename = "fringe-benefit-" + (marca || 'veicolo') + ".pdf";
      downloadFile(pdfBlob, filename);
      toast.success("PDF scaricato!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Errore nell'esportazione PDF");
    }
  };

  // Export Excel - Single Vehicle
  const exportSingleExcel = () => {
    if (!result) return;
    
    try {
      const tipoOp = orderType === "nuovo_ordine" ? "Nuovo Ordine" : (isPre2025 ? "Riassegnazione (Pre 2025)" : "Riassegnazione (Post 2025)");
      
      const data = [
        {
          "Marca": marca || "-",
          "Modello": modello || "-",
          "Tipo di Operazione": tipoOp,
          "Totale Annuo": result.fringe_benefit_annuo,
          "Totale Mensile": result.fringe_benefit_mensile
        }
      ];
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fringe Benefit");
      
      // Set column widths
      ws['!cols'] = [
        { wch: 15 }, // Marca
        { wch: 30 }, // Modello
        { wch: 25 }, // Tipo di Operazione
        { wch: 15 }, // Totale Annuo
        { wch: 15 }  // Totale Mensile
      ];
      
      // Generate blob and download
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = "fringe-benefit-" + (marca || 'veicolo') + ".xlsx";
      downloadFile(blob, filename);
      toast.success("Excel scaricato!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Errore nell'esportazione Excel");
    }
  };

  // Export PDF - Fleet
  const exportFleetPDF = () => {
    if (fleet.length === 0) return;
    
    try {
      const doc = new jsPDF();
      const today = new Date().toLocaleDateString('it-IT');
      
      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.text("FringeCalculator", 20, 25);
      doc.setFontSize(10);
      doc.text("Report Flotta del " + today, 140, 25);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.text("Riepilogo Flotta Aziendale", 20, 55);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(fleet.length + " veicoli - Azienda: " + (user?.azienda || 'N/A'), 20, 63);
      
      // Table header
      let yPos = 80;
      doc.setFillColor(241, 245, 249);
      doc.rect(15, yPos - 5, 180, 10, 'F');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text("MARCA", 18, yPos);
      doc.text("MODELLO", 45, yPos);
      doc.text("TIPO OPERAZIONE", 95, yPos);
      doc.text("TOTALE ANNUO", 140, yPos);
      doc.text("TOTALE MENSILE", 170, yPos);
      
      yPos += 10;
      
      // Table rows
      doc.setTextColor(0, 0, 0);
      fleet.forEach((vehicle, index) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 30;
          // Reprint header on new page
          doc.setFillColor(241, 245, 249);
          doc.rect(15, yPos - 5, 180, 10, 'F');
          doc.setFontSize(8);
          doc.setTextColor(71, 85, 105);
          doc.text("MARCA", 18, yPos);
          doc.text("MODELLO", 45, yPos);
          doc.text("TIPO OPERAZIONE", 95, yPos);
          doc.text("TOTALE ANNUO", 140, yPos);
          doc.text("TOTALE MENSILE", 170, yPos);
          yPos += 10;
          doc.setTextColor(0, 0, 0);
        }
        
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(15, yPos - 4, 180, 8, 'F');
        }
        
        doc.setFontSize(8);
        doc.text(vehicle.marca, 18, yPos);
        doc.text(vehicle.modello.substring(0, 25), 45, yPos);
        doc.text(getTipoOperazione(vehicle), 95, yPos);
        doc.text("EUR " + vehicle.fringe_benefit_annuo.toFixed(2), 140, yPos);
        doc.text("EUR " + vehicle.fringe_benefit_mensile.toFixed(2), 170, yPos);
        
        yPos += 8;
      });
      
      // Totals row
      yPos += 5;
      doc.setFillColor(16, 185, 129);
      doc.rect(15, yPos - 4, 180, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text("TOTALE FLOTTA", 18, yPos + 2);
      doc.text("EUR " + fleetTotals.annuo.toFixed(2), 140, yPos + 2);
      doc.text("EUR " + fleetTotals.mensile.toFixed(2), 170, yPos + 2);
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text("Documento generato da FringeCalculator - Basato su Tabelle ACI 2026", 20, 280);
      
      // Generate blob and download
      const pdfBlob = doc.output('blob');
      const filename = "flotta-fringe-benefit-" + today.replace(/\//g, '-') + ".pdf";
      downloadFile(pdfBlob, filename);
      toast.success("PDF flotta scaricato!");
    } catch (error) {
      console.error("PDF fleet export error:", error);
      toast.error("Errore nell'esportazione PDF flotta");
    }
  };

  // Export Excel - Fleet
  const exportFleetExcel = () => {
    if (fleet.length === 0) return;
    
    try {
      const data = fleet.map(vehicle => ({
        "Marca": vehicle.marca,
        "Modello": vehicle.modello,
        "Tipo di Operazione": getTipoOperazione(vehicle),
        "Totale Annuo": vehicle.fringe_benefit_annuo,
        "Totale Mensile": vehicle.fringe_benefit_mensile
      }));
      
      // Add totals row
      data.push({
        "Marca": "TOTALE",
        "Modello": "",
        "Tipo di Operazione": "",
        "Totale Annuo": fleetTotals.annuo,
        "Totale Mensile": fleetTotals.mensile
      });
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Flotta");
      
      // Set column widths
      ws['!cols'] = [
        { wch: 15 }, // Marca
        { wch: 35 }, // Modello
        { wch: 25 }, // Tipo di Operazione
        { wch: 15 }, // Totale Annuo
        { wch: 15 }  // Totale Mensile
      ];
      
      // Generate blob and download
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
      const filename = "flotta-fringe-benefit-" + today + ".xlsx";
      downloadFile(blob, filename);
      toast.success("Excel flotta scaricato!");
    } catch (error) {
      console.error("Excel fleet export error:", error);
      toast.error("Errore nell'esportazione Excel flotta");
    }
  };

  // Fleet totals
  const fleetTotals = {
    annuo: fleet.reduce((sum, v) => sum + v.fringe_benefit_annuo, 0),
    mensile: fleet.reduce((sum, v) => sum + v.fringe_benefit_mensile, 0),
    count: fleet.length
  };

  // Validation
  const canCalculateNuovoOrdine = fuelType !== "" && costoKm !== "";
  const canCalculateRiassegnazione = isPre2025 !== null && marca && modello && costoKm && (
    !isPre2025 || (canoneNoleggio && kmMensili)
  );
  const canCalculate = orderType === "nuovo_ordine" ? canCalculateNuovoOrdine : canCalculateRiassegnazione;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Calcolatore
              </span>
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <span className="text-sm text-slate-600 hidden sm:inline">
                  {user.azienda}
                </span>
              )}
              {/* Admin button - visible only for admin email */}
              {user?.email?.toLowerCase() === "stefano.albera@partners.basf.com" && (
                <button 
                  onClick={() => navigate('/admin')}
                  className="px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition-colors"
                  data-testid="admin-panel-btn"
                >
                  Admin
                </button>
              )}
              <button 
                onClick={() => {
                  onLogout?.();
                  navigate('/');
                }}
                className="text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="logout-btn"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mode Selector */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-white rounded-lg p-1 border border-slate-200">
            <button
              onClick={() => { setMode("single"); setFleet([]); setResult(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                mode === "single" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="mode-single"
            >
              <Car className="w-4 h-4 inline mr-2" />
              Singolo Veicolo
            </button>
            <button
              onClick={() => { setMode("fleet"); setResult(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                mode === "fleet" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="mode-fleet"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Flotta Aziendale
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-3 space-y-6">
            {/* Fleet info banner */}
            {mode === "fleet" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Modalità Flotta</p>
                  <p className="text-sm text-blue-700">Aggiungi più veicoli e calcola il totale del fringe benefit aziendale</p>
                </div>
              </div>
            )}

            {/* Step 1: Tipo Ordine */}
            <Card className="calculator-card animate-fade-in" data-testid="step-1-card">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="step-circle step-active">1</div>
                  <CardTitle className="text-lg">Tipo di Operazione</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={orderType} 
                  onValueChange={(value) => {
                    setOrderType(value);
                    setResult(null);
                  }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem value="nuovo_ordine" id="nuovo_ordine" className="peer sr-only" />
                    <Label
                      htmlFor="nuovo_ordine"
                      className="flex flex-col items-center justify-center p-6 border-2 rounded-xl cursor-pointer transition-all peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:border-blue-300"
                      data-testid="nuovo-ordine-option"
                    >
                      <Car className="w-8 h-8 mb-2 text-blue-600" />
                      <span className="font-semibold text-slate-900">Nuovo Ordine</span>
                      <span className="text-sm text-slate-500 mt-1">Auto nuova dal 2025</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="riassegnazione" id="riassegnazione" className="peer sr-only" />
                    <Label
                      htmlFor="riassegnazione"
                      className="flex flex-col items-center justify-center p-6 border-2 rounded-xl cursor-pointer transition-all peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:border-blue-300"
                      data-testid="riassegnazione-option"
                    >
                      <RotateCcw className="w-8 h-8 mb-2 text-blue-600" />
                      <span className="font-semibold text-slate-900">Riassegnazione</span>
                      <span className="text-sm text-slate-500 mt-1">Auto già in uso</span>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* RIASSEGNAZIONE: Step 2 - Pre/Post 2025 */}
            {orderType === "riassegnazione" && (
              <Card className="calculator-card animate-slide-up" data-testid="step-immatricolazione">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="step-circle step-active">2</div>
                    <CardTitle className="text-lg">Data Immatricolazione</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <RadioGroup 
                    value={isPre2025 === null ? "" : (isPre2025 ? "pre" : "post")} 
                    onValueChange={(value) => setIsPre2025(value === "pre")}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <div>
                      <RadioGroupItem value="pre" id="pre2025" className="peer sr-only" />
                      <Label
                        htmlFor="pre2025"
                        className="flex flex-col items-center justify-center p-5 border-2 rounded-xl cursor-pointer transition-all peer-checked:border-amber-500 peer-checked:bg-amber-50 hover:border-amber-300"
                        data-testid="pre-2025-option"
                      >
                        <Calendar className="w-6 h-6 mb-2 text-amber-600" />
                        <span className="font-semibold text-slate-900">Prima del 01/01/2025</span>
                        <span className="text-xs text-slate-500 mt-1">Valore Normale</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="post" id="post2025" className="peer sr-only" />
                      <Label
                        htmlFor="post2025"
                        className="flex flex-col items-center justify-center p-5 border-2 rounded-xl cursor-pointer transition-all peer-checked:border-emerald-500 peer-checked:bg-emerald-50 hover:border-emerald-300"
                        data-testid="post-2025-option"
                      >
                        <Calendar className="w-6 h-6 mb-2 text-emerald-600" />
                        <span className="font-semibold text-slate-900">Dal 01/01/2025</span>
                        <span className="text-xs text-slate-500 mt-1">Tabelle ACI</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            )}

            {/* Step: Selezione Veicolo */}
            {(orderType === "nuovo_ordine" || (orderType === "riassegnazione" && isPre2025 !== null)) && (
              <Card className="calculator-card animate-slide-up" data-testid="step-veicolo">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="step-circle step-active">
                      {orderType === "riassegnazione" ? "3" : "2"}
                    </div>
                    <CardTitle className="text-lg">Seleziona Veicolo</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Marca & Modello */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Marca</Label>
                      <Select value={marca} onValueChange={setMarca}>
                        <SelectTrigger data-testid="marca-select">
                          <SelectValue placeholder="Seleziona marca" />
                        </SelectTrigger>
                        <SelectContent>
                          {marche.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Modello with Autocomplete */}
                    <div className="space-y-2">
                      <Label>Modello</Label>
                      <div className="relative" ref={modelloInputRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder={marca ? "Cerca modello..." : "Prima seleziona marca"}
                            value={modelloSearch}
                            onChange={(e) => {
                              setModelloSearch(e.target.value);
                              setShowModelloDropdown(true);
                            }}
                            onFocus={() => marca && setShowModelloDropdown(true)}
                            disabled={!marca}
                            className="pl-10 h-11"
                            data-testid="modello-search-input"
                          />
                          {searchingModello && (
                            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                          )}
                        </div>
                        
                        {/* Dropdown suggestions */}
                        {showModelloDropdown && marca && modelloSuggestions.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {modelloSuggestions.map((item, index) => (
                              <button
                                key={item.id || index}
                                type="button"
                                className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                                onClick={() => handleModelloSelect(item)}
                                data-testid={`modello-suggestion-${index}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-slate-900 text-sm">{item.modello}</p>
                                    <p className="text-xs text-slate-500">
                                      {fuelTypeConfig[item.alimentazione]?.label || item.alimentazione}
                                      {item.in_produzione === false && " • Fuori prod."}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-mono text-sm text-blue-600">
                                      €{item.costo_km?.toFixed(4)}
                                    </p>
                                    <p className="text-xs text-slate-400">/km</p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {showModelloDropdown && marca && modelloSearch && modelloSuggestions.length === 0 && !searchingModello && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center text-slate-500">
                            Nessun modello trovato
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Fuel Type - Solo per nuovo ordine o riassegnazione post-2025 */}
                  {(orderType === "nuovo_ordine" || !isPre2025) && (
                    <div className="space-y-3">
                      <Label>Tipo di Alimentazione {fuelType && <span className="text-emerald-600 text-xs">(auto-selezionato)</span>}</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {Object.entries(fuelTypeConfig).map(([key, config]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setFuelType(key)}
                            className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                              fuelType === key 
                                ? `${config.color} border-current` 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`fuel-type-${key}`}
                          >
                            {config.icon}
                            <span className="text-xs font-medium mt-1">{config.label}</span>
                            <span className="text-xs text-slate-500">{config.percentage}%</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Costo Km - info */}
                  {costoKm && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Costo chilometrico:</span>
                        <span className="font-mono font-medium">€{parseFloat(costoKm).toFixed(4)}/km</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm text-slate-600">Costo totale 15.000 km:</span>
                        <span className="font-mono font-medium">€{(parseFloat(costoKm) * 15000).toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* RIASSEGNAZIONE PRE-2025: Dati per Valore Normale */}
            {orderType === "riassegnazione" && isPre2025 === true && modello && (
              <Card className="calculator-card animate-slide-up" data-testid="step-valore-normale">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="step-circle step-active">4</div>
                    <CardTitle className="text-lg">Dati per Valore Normale</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Formula:</strong> Canone - Optional - (Km mensili × Costo km)
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Canone Mensile (€)</Label>
                      <Input
                        type="number"
                        placeholder="Es. 500"
                        value={canoneNoleggio}
                        onChange={(e) => setCanoneNoleggio(e.target.value)}
                        className="h-11"
                        data-testid="canone-noleggio-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Km Mensili</Label>
                      <Input
                        type="number"
                        placeholder="Es. 1500"
                        value={kmMensili}
                        onChange={(e) => setKmMensili(e.target.value)}
                        className="h-11"
                        data-testid="km-mensili-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Optional Dipendente (€)</Label>
                      <Input
                        type="number"
                        placeholder="Es. 50 (opz.)"
                        value={optionalDipendente}
                        onChange={(e) => setOptionalDipendente(e.target.value)}
                        className="h-11"
                        data-testid="optional-dipendente-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Calculate Button */}
            {canCalculate && (
              <div className="flex gap-4 animate-fade-in">
                <Button
                  variant="outline"
                  className="flex-1 h-12"
                  onClick={handleReset}
                  data-testid="reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Ricomincia
                </Button>
                <Button
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                  onClick={handleCalculate}
                  disabled={loading}
                  data-testid="calculate-btn"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Calcolo...
                    </span>
                  ) : mode === "fleet" ? (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi alla Flotta
                    </>
                  ) : (
                    <>Calcola Fringe Benefit</>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Result Section - Sticky */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-4">
              {/* Single Result Card */}
              {mode === "single" && (
                <Card className={`overflow-hidden transition-all duration-500 ${result ? 'result-card' : 'calculator-card'}`} data-testid="result-card">
                  <CardContent className={`p-6 ${result ? 'text-white' : ''}`}>
                    {result ? (
                      <div className="animate-fade-in">
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          <span className="text-slate-300 text-sm">Calcolo Completato</span>
                        </div>
                        
                        <div className="mb-6">
                          <p className="text-slate-400 text-sm mb-1">Fringe Benefit Annuo</p>
                          <p className="text-4xl font-bold number-display" data-testid="result-annual">
                            € {result.fringe_benefit_annuo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        <div className="mb-6 pb-6 border-b border-slate-700">
                          <p className="text-slate-400 text-sm mb-1">Fringe Benefit Mensile</p>
                          <p className="text-2xl font-bold number-display" data-testid="result-monthly">
                            € {result.fringe_benefit_mensile.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Metodo</span>
                            <span className="font-medium">
                              {result.metodo_calcolo === "tabelle_aci_2026" ? "Tabelle ACI 2026" : "Valore Normale"}
                            </span>
                          </div>
                          {result.percentuale_applicata > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-400">Percentuale</span>
                              <span className="font-medium">{result.percentuale_applicata}%</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-slate-400">Costo km</span>
                            <span className="font-mono">€{result.dettagli.costo_km?.toFixed(4)}/km</span>
                          </div>
                          {modello && (
                            <div className="flex justify-between">
                              <span className="text-slate-400">Veicolo</span>
                              <span className="font-medium text-right text-xs">{marca} {modello.substring(0, 25)}{modello.length > 25 ? '...' : ''}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Export Dropdown Button */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              className="w-full mt-6 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                              data-testid="export-single-btn"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Esporta
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="w-48">
                            <DropdownMenuItem onClick={exportSinglePDF} className="cursor-pointer">
                              <FileText className="w-4 h-4 mr-2 text-red-500" />
                              Esporta PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={exportSingleExcel} className="cursor-pointer">
                              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                              Esporta Excel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Car className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-2">Risultato Calcolo</h3>
                        <p className="text-slate-500 text-sm">
                          Compila il form per calcolare il fringe benefit
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fleet Result Card */}
              {mode === "fleet" && (
                <Card className="overflow-hidden" data-testid="fleet-card">
                  <CardHeader className="bg-slate-900 text-white pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Riepilogo Flotta
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {fleet.length === 0 ? (
                      <div className="text-center py-8 px-4">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Users className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-2">Nessun Veicolo</h3>
                        <p className="text-slate-500 text-sm">
                          Aggiungi veicoli per vedere il totale della flotta
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Totals */}
                        <div className="bg-slate-900 text-white p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-slate-400 text-xs">Totale Annuo</p>
                              <p className="text-2xl font-bold number-display">
                                € {fleetTotals.annuo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                            <div>
                              <p className="text-slate-400 text-xs">Totale Mensile</p>
                              <p className="text-2xl font-bold number-display">
                                € {fleetTotals.mensile.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                          <p className="text-slate-400 text-xs mt-2">{fleetTotals.count} veicoli nella flotta</p>
                        </div>
                        
                        {/* Vehicle List */}
                        <div className="max-h-80 overflow-y-auto">
                          {fleet.map((vehicle, index) => (
                            <div 
                              key={vehicle.id} 
                              className="flex items-center justify-between p-4 border-b border-slate-100 hover:bg-slate-50"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                  {vehicle.marca} {vehicle.modello.substring(0, 20)}{vehicle.modello.length > 20 ? '...' : ''}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {fuelTypeConfig[vehicle.alimentazione]?.label} • {vehicle.percentuale > 0 ? `${vehicle.percentuale}%` : 'Valore Normale'}
                                </p>
                              </div>
                              <div className="text-right ml-4">
                                <p className="font-mono font-medium text-slate-900">
                                  € {vehicle.fringe_benefit_annuo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-xs text-slate-500">/anno</p>
                              </div>
                              <button
                                onClick={() => removeFromFleet(vehicle.id)}
                                className="ml-3 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`remove-vehicle-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Export Fleet Dropdown Button */}
                        <div className="p-4 border-t border-slate-200">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                data-testid="export-fleet-btn"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Esporta Flotta
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center" className="w-48">
                              <DropdownMenuItem onClick={exportFleetPDF} className="cursor-pointer">
                                <FileText className="w-4 h-4 mr-2 text-red-500" />
                                Esporta PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={exportFleetExcel} className="cursor-pointer">
                                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                                Esporta Excel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Info Box */}
              <Card className="border-blue-200 bg-blue-50" data-testid="info-box">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Database ACI Ufficiale</p>
                      <p className="text-blue-700">
                        <strong>4.695 veicoli</strong> dalle tabelle ACI 2026.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      {/* Click outside handler */}
      {showModelloDropdown && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setShowModelloDropdown(false)}
        />
      )}
    </div>
  );
};

export default CalculatorPage;
